import {
  AfterViewInit,
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  ElementRef,
  ViewChild,
  signal,
  inject,
  computed,
  PLATFORM_ID,
} from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { ThemeToggleComponent } from '../theme-toggle/theme-toggle.component';
import { ThemeService } from '../../../core/services/theme.service';
import { LanguageService } from '../../../core/services/language.service';
import { DOCUMENT, isPlatformBrowser, UpperCasePipe } from '@angular/common';
import { TranslocoPipe } from '@jsverse/transloco';
import { environment } from '../../../../environments/environment';
import { ConversionClickDirective } from '../../../core/directives/conversion-click.directive';

interface MegaMenuItem {
  label: string;
  path: string;
  queryParams?: Record<string, string>;
  icon: string;
  description: string;
  details?: string;
  highlight?: string;
  color: string;
}

interface MegaMenuSubCategory {
  id: string;
  label: string;
  icon: string;
  items: MegaMenuItem[];
}

interface MegaMenuCategory {
  id: string;
  label: string;
  labelKey: string;
  subcategories: MegaMenuSubCategory[];
}

@Component({
  selector: 'app-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, RouterLinkActive, ThemeToggleComponent, TranslocoPipe, UpperCasePipe, ConversionClickDirective],
  host: {
    '(window:scroll)': 'onScroll()',
    '(document:click)': 'onDocumentClick($event)',
    '(document:keydown.escape)': 'closeAll()',
    style: 'display: contents',
  },
  templateUrl: './header.component.html',
  styleUrl: './header.component.css',
})
export class HeaderComponent implements AfterViewInit {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('shell') private readonly shellRef?: ElementRef<HTMLElement>;
  protected readonly theme = inject(ThemeService);
  protected readonly lang = inject(LanguageService);
  protected readonly panelUrl = environment.panelUrl;
  protected readonly registerUrl = `${environment.panelUrl}/register`;
  protected readonly logoSrc = computed(() => (this.theme.isDark() ? 'logo/logo_dark.svg' : 'logo/logo_light.svg'));

  /** Header décroché en pilule flottante (après avoir dépassé le bandeau de 150px). */
  protected readonly floating = signal(false);
  protected readonly mobileOpen = signal(false);
  protected readonly mobileAccordion = signal<string | null>(null);
  protected readonly activeMegaMenu = signal<string | null>(null);
  protected readonly activeSubCategory = signal<string | null>(null);
  protected readonly hoveredItem = signal<MegaMenuItem | null>(null);
  protected readonly langOpen = signal(false);

  constructor() {
    this.destroyRef.onDestroy(() => this.setPageScrollLocked(false));
  }

  protected readonly currentLanguage = computed(
    () => this.lang.languages.find((l) => l.code === this.lang.currentLang()) ?? this.lang.languages[0]
  );

  protected toggleLang(): void {
    this.langOpen.update((v) => !v);
  }

  protected onDocumentClick(event: MouseEvent): void {
    if (!this.langOpen()) return;
    const target = event.target as HTMLElement;
    if (!target.closest('.lang-select')) {
      this.langOpen.set(false);
    }
  }

  protected selectLang(code: string): void {
    this.lang.switchLang(code);
    this.langOpen.set(false);
  }

  private closeTimeout: ReturnType<typeof setTimeout> | null = null;

  ngAfterViewInit(): void {
    this.onScroll();
  }

  protected onScroll(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const y = window.scrollY;
    const headerHeight = this.shellRef?.nativeElement.offsetHeight ?? 120;
    // Décroche 150px après avoir dépassé le header ; hystérésis pour éviter tout clignotement.
    const wasFloating = this.floating();
    if (y > headerHeight + 150) {
      this.floating.set(true);
    } else if (y < headerHeight + 90) {
      this.floating.set(false);
    }
    // Referme les menus ouverts quand la barre change d'état (leur ancrage change).
    if (wasFloating !== this.floating() && this.activeMegaMenu() !== null) {
      this.activeMegaMenu.set(null);
      this.activeSubCategory.set(null);
    }
  }

  protected keepMegaMenuOpen(): void {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
  }

  protected readonly megaMenuCategories: MegaMenuCategory[] = [
    {
      id: 'cloud',
      label: 'header.menu.cloud.label',
      labelKey: 'header.navCloud',
      // Miroir exact de l'univers Cloud du mega menu desktop.
      subcategories: [
        {
          id: 'offres',
          label: 'header.menu.cloud.label',
          icon: 'M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2',
          items: [
            {
              label: 'header.cloud.vpsStandard',
              path: '/vps-standard',
              queryParams: { type: 'kvm' },
              icon: 'M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2',
              description: 'header.cloud.vpsStandardDesc',
              color: '#3b82f6',
            },
            {
              label: 'header.cloud.vpsPerformance',
              path: '/vps-performance',
              queryParams: { type: 'kvm' },
              icon: 'M13 10V3L4 14h7v7l9-11h-7z',
              description: 'header.cloud.vpsPerformanceDesc',
              color: '#00AEFF',
            },
            {
              label: 'header.cloud.pleskDedicated',
              path: '/plesk-dedicated',
              icon: 'M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z',
              description: 'header.cloud.pleskDedicatedDesc',
              color: '#8b5cf6',
            },
            {
              label: 'header.cloud.dedicated',
              path: '/dedicated',
              icon: 'M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z',
              description: 'header.cloud.dedicatedDesc',
              color: '#0ea5e9',
            },
            {
              label: 'header.cloud.antiDdos',
              path: '/antiddos',
              icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
              description: 'header.cloud.antiDdosPromo',
              color: '#142149',
            },
          ],
        },
      ],
    },
    {
      id: 'network',
      label: 'header.menu.network.label',
      labelKey: 'header.navNetwork',
      subcategories: [
        {
          id: 'datacenter',
          label: 'header.menu.network.datacenter.label',
          icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
          items: [
            {
              label: 'header.network.datacenter',
              path: '/datacenter',
              icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
              description: 'header.network.datacenterDesc',
              color: '#a855f7',
            },
          ],
        },
        {
          id: 'network',
          label: 'header.menu.network.network.label',
          icon: 'M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.14 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0',
          items: [
            {
              label: 'header.menu.network.network.tunnels.label',
              path: '/ip-tunnels',
              icon: 'M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.14 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0',
              description: 'header.menu.network.network.tunnels.description',
              details: 'header.menu.network.network.tunnels.details',
              highlight: 'header.menu.network.network.tunnels.highlight',
              color: '#3b82f6',
            },
            {
              label: 'header.menu.network.network.ddos.label',
              path: '/antiddos',
              icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
              description: 'header.menu.network.network.ddos.description',
              details: 'header.menu.network.network.ddos.details',
              highlight: 'header.menu.network.network.ddos.highlight',
              color: '#8b5cf6',
            },
          ],
        },
      ],
    },
    {
      id: 'web',
      label: 'header.menu.web.label',
      labelKey: 'header.navWeb',
      subcategories: [
        {
          id: 'domains',
          label: 'header.menu.web.domains.label',
          icon: 'M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9',
          items: [
            {
              label: 'header.menu.web.domains.register.label',
              path: '/domains',
              icon: 'M12 4v16m8-8H4',
              description: 'header.menu.web.domains.register.description',
              details: 'header.menu.web.domains.register.details',
              highlight: 'header.menu.web.domains.register.highlight',
              color: '#3b82f6',
            },
            {
              label: 'header.menu.web.domains.transfer.label',
              path: '/domains',
              icon: 'M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4',
              description: 'header.menu.web.domains.transfer.description',
              details: 'header.menu.web.domains.transfer.details',
              highlight: 'header.menu.web.domains.transfer.highlight',
              color: '#10b981',
            },
            {
              label: 'header.menu.web.domains.whois.label',
              path: '/domains',
              icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
              description: 'header.menu.web.domains.whois.description',
              details: 'header.menu.web.domains.whois.details',
              highlight: 'header.menu.web.domains.whois.highlight',
              color: '#8b5cf6',
            },
          ],
        },
        {
          id: 'hosting',
          label: 'header.menu.web.hosting.label',
          icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
          items: [
            {
              label: 'header.menu.web.hosting.plesk.label',
              path: '/plesk',
              icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
              description: 'header.menu.web.hosting.plesk.description',
              details: 'header.menu.web.hosting.plesk.details',
              highlight: 'header.menu.web.hosting.plesk.highlight',
              color: '#10b981',
            },
            {
              label: 'header.menu.web.hosting.ssl.label',
              path: '/ssl',
              icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
              description: 'header.menu.web.hosting.ssl.description',
              details: 'header.menu.web.hosting.ssl.details',
              highlight: 'header.menu.web.hosting.ssl.highlight',
              color: '#10b981',
            },
            {
              label: 'header.menu.web.hosting.dns.label',
              path: '/domains',
              icon: 'M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4',
              description: 'header.menu.web.hosting.dns.description',
              details: 'header.menu.web.hosting.dns.details',
              highlight: 'header.menu.web.hosting.dns.highlight',
              color: '#3b82f6',
            },
          ],
        },
      ],
    },
    {
      id: 'enterprise',
      label: 'header.menu.enterprise.label',
      labelKey: 'header.navEnterprise',
      subcategories: [
        {
          id: 'connectivity',
          label: 'header.menu.enterprise.connectivity.label',
          icon: 'M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.14 0',
          items: [
            {
              label: 'header.menu.enterprise.connectivity.m365.label',
              path: '/workspace',
              icon: 'M4 5a1 1 0 011-1h6v7H4V5zm9-1h6a1 1 0 011 1v6h-7V4zM4 13h7v7H5a1 1 0 01-1-1v-6zm9 0h7v6a1 1 0 01-1 1h-6v-7z',
              description: 'header.menu.enterprise.connectivity.m365.description',
              details: 'header.menu.enterprise.connectivity.m365.details',
              highlight: 'header.menu.enterprise.connectivity.m365.highlight',
              color: '#3b82f6',
            },
            {
              label: 'header.menu.enterprise.connectivity.google.label',
              path: '/workspace',
              icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
              description: 'header.menu.enterprise.connectivity.google.description',
              details: 'header.menu.enterprise.connectivity.google.details',
              highlight: 'header.menu.enterprise.connectivity.google.highlight',
              color: '#10b981',
            },
            {
              label: 'header.menu.enterprise.connectivity.ftto.label',
              path: '/fibre',
              icon: 'M13 10V3L4 14h7v7l9-11h-7z',
              description: 'header.menu.enterprise.connectivity.ftto.description',
              details: 'header.menu.enterprise.connectivity.ftto.details',
              highlight: 'header.menu.enterprise.connectivity.ftto.highlight',
              color: '#10b981',
            },
            {
              label: 'header.menu.enterprise.connectivity.ftth.label',
              path: '/fibre',
              icon: 'M13 10V3L4 14h7v7l9-11h-7z',
              description: 'header.menu.enterprise.connectivity.ftth.description',
              details: 'header.menu.enterprise.connectivity.ftth.details',
              highlight: 'header.menu.enterprise.connectivity.ftth.highlight',
              color: '#10b981',
            },
            {
              label: 'header.menu.enterprise.connectivity.sip.label',
              path: '/telephonie',
              icon: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
              description: 'header.menu.enterprise.connectivity.sip.description',
              details: 'header.menu.enterprise.connectivity.sip.details',
              highlight: 'header.menu.enterprise.connectivity.sip.highlight',
              color: '#10b981',
            },
          ],
        },
      ],
    },
  ];

  protected toggleMobile(): void {
    const open = !this.mobileOpen();
    if (open) {
      this.syncMobileMenuTop();
    }
    this.mobileOpen.set(open);
    this.setPageScrollLocked(open);
    if (!open) {
      this.mobileAccordion.set(null);
    }
  }

  protected closeMobile(): void {
    this.mobileOpen.set(false);
    this.mobileAccordion.set(null);
    this.setPageScrollLocked(false);
  }

  private setPageScrollLocked(locked: boolean): void {
    if (!isPlatformBrowser(this.platformId)) return;
    // Le clip horizontal posé sur <html> casse la propagation de l'overflow du
    // body vers le viewport : il faut verrouiller les deux éléments.
    this.document.body.classList.toggle('mobile-navigation-open', locked);
    this.document.documentElement.classList.toggle('mobile-navigation-open', locked);
  }
  /** Aligne le panneau mobile sur le bas réel de la barre (flottante ou non). */
  private syncMobileMenuTop(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const bar = this.document.querySelector('.header-bar');
    const bottom = bar ? Math.max(0, Math.round(bar.getBoundingClientRect().bottom)) : 96;
    this.document.documentElement.style.setProperty('--mobile-menu-top', `${bottom}px`);
  }

  protected toggleMobileAccordion(id: string): void {
    this.mobileAccordion.update((current) => (current === id ? null : id));
  }

  protected setActiveSubCategory(id: string): void {
    this.activeSubCategory.set(id);
    this.hoveredItem.set(null);
  }

  protected setHoveredItem(item: MegaMenuItem): void {
    this.hoveredItem.set(item);
  }

  protected clearHoveredItem(): void {
    this.hoveredItem.set(null);
  }

  protected openMegaMenu(id: string): void {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
    this.activeMegaMenu.set(id);
    this.activeSubCategory.set(null);
    this.hoveredItem.set(null);
  }

  protected closeMegaMenu(): void {
    this.closeTimeout = setTimeout(() => {
      this.activeMegaMenu.set(null);
      this.closeTimeout = null;
    }, 100);
  }

  protected closeAll(): void {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
    this.closeMobile();
    this.activeMegaMenu.set(null);
    this.hoveredItem.set(null);
    this.langOpen.set(false);
  }
}
